// Register.tsx
import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link as MuiLink,
  Paper,
  Divider,
  Stack,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AuthService } from '../../../../api/services/AuthService';
import { UsersService } from '../../../../api/services/UsersService';
import { OpenAPI } from '../../../../api/core/OpenAPI';

const brandImgs = [
  { src: '/reddit.png', size: 120, top: '6%', left: '8%', rot: -8 },
  { src: '/google.png', size: 130, top: '10%', right: '8%', rot: 10 },
  { src: '/youtube.png', size: 140, bottom: '10%', left: '10%', rot: -6 },
  { src: '/stacko.png', size: 120, bottom: '14%', right: '12%', rot: 6 },
  { src: '/x.webp', size: 110, top: '40%', left: '45%', rot: 0 },
  { src: '/gyik.png', size: 120, top: '68%', left: '30%', rot: -4 },
];

// Az API kliens alapjára szükségünk lehet (pl. avatar URL megjelenítéshez a preview-hoz, de itt csak file preview van)
const API_BASE = OpenAPI.BASE || 'http://localhost:5051';

export default function Register() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Profilkép fájlfeltöltés
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // UI állapotok
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Kitöltés utáni átirányítás
  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => navigate('/login'), 1200);
      return () => clearTimeout(t);
    }
  }, [successMsg, navigate]);

  // Fájl előnézet
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const initials = useMemo(() => {
    const src = displayName?.trim() || email?.split('@')[0] || 'U';
    return src
      .split(/[ ._-]+/g)
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }, [displayName, email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg(null);

    if (!email.trim()) {
      setError('Add meg az email címet');
      return;
    }

    if (password !== confirmPassword) {
      setError('A jelszavak nem egyeznek');
      return;
    }

    setSubmitting(true);
    try {
      // 1) Regisztráció (displayName opcionális, profilképet nem URL-lel, hanem utólag fájlal töltjük fel)
      await AuthService.postApiAuthRegister({
        email: email.trim(),
        password,
        displayName: displayName.trim() || null,
        profileImageUrl: null, // itt NEM adunk meg URL-t, mert fájlt töltünk
      } as any);

      // 2) Automatikus bejelentkezés a későbbi avatar uploadhoz (cookie)
      await AuthService.postApiAuthLogin({ email: email.trim(), password });

      // 3) Ha van kiválasztott fájl, avatar feltöltés multiparttal
      if (file) {
        // A generált kliens multipart 'file' kulcsot vár
        // @ts-expect-error – a UsersService egyedi request wrapper-t használ
        await UsersService.postApiUsersMeAvatar({ file });
      }

      // 4) Kijelentkezés (hogy a /login tiszta állapotban fogadjon)
      await AuthService.postApiAuthLogout().catch(() => {});

      setSuccessMsg('Sikeres regisztráció!');
    } catch (err: any) {
      // ASP.NET Identity hibák
      const body = err?.body;
      if (body?.errors) {
        const msgs = body.errors.map((x: any) =>
          x.code === 'DuplicateUserName' ? 'Ez az email cím már foglalt' : x.description
        );
        setError(msgs.join('\n'));
      } else if (typeof err?.message === 'string') {
        setError(err.message);
      } else {
        setError('Hiba történt a regisztráció során');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      {/* 1) Erős gradient alap (mode-függő) */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          backgroundColor: (t) => (t.palette.mode === 'dark' ? '#0b0f17' : '#f6f8fd'),
          backgroundImage: (t) =>
            t.palette.mode === 'dark'
              ? [
                  'radial-gradient(1000px 600px at 8% -10%, rgba(99,102,241,.25), transparent 60%)',
                  'radial-gradient(900px 540px at 110% 10%, rgba(16,185,129,.22), transparent 55%)',
                  'linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,.02))',
                ].join(', ')
              : [
                  'radial-gradient(1000px 600px at 8% -10%, rgba(99,102,241,.18), transparent 60%)',
                  'radial-gradient(900px 540px at 110% 10%, rgba(16,185,129,.16), transparent 55%)',
                  'linear-gradient(180deg, rgba(0,0,0,.03), rgba(0,0,0,.02))',
                ].join(', '),
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      />

      {/* 2) Ikon-kollázs (blur + blend) */}
      {brandImgs.map((b, i) => (
        <Box
          aria-hidden
          key={i}
          component="img"
          src={b.src}
          alt=""
          sx={{
            position: 'fixed',
            zIndex: 0,
            ...(b.top ? { top: b.top } : {}),
            ...(b.bottom ? { bottom: b.bottom } : {}),
            ...(b.left ? { left: b.left } : {}),
            ...(b.right ? { right: b.right } : {}),
            width: b.size,
            height: b.size,
            opacity: (t) => (t.palette.mode === 'dark' ? 0.12 : 0.1),
            filter: (t) =>
              t.palette.mode === 'dark'
                ? 'grayscale(1) blur(1.2px) brightness(1.35)'
                : 'grayscale(1) blur(1px) brightness(0.85)',
            transform: `rotate(${b.rot}deg)`,
            mixBlendMode: (t) => (t.palette.mode === 'dark' ? 'screen' : 'multiply'),
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      ))}

      {/* 3) Grid-overlay */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          backgroundImage: (t) =>
            t.palette.mode === 'dark'
              ? 'linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)'
              : 'linear-gradient(rgba(0,0,0,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px, 28px 28px',
          maskImage:
            'radial-gradient(1200px 800px at 50% 40%, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Két hasábos layout (hero + kártya) */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          alignItems: 'center',
          gap: { xs: 2, md: 6 },
          maxWidth: 1100,
          mx: 'auto',
          px: { xs: 2, sm: 4, md: 6 },
          py: { xs: 6, md: 10 },
          minHeight: '100vh',
        }}
      >
        {/* Bal: hero szöveg */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              letterSpacing: -0.5,
              mb: 1,
              textShadow: (t) =>
                t.palette.mode === 'dark'
                  ? '0 1px 0 rgba(0,0,0,.6)'
                  : '0 1px 0 rgba(255,255,255,.8)',
            }}
          >
            Csatlakozz és elemezz!
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, maxWidth: 640, lineHeight: 1.5 }}>
            Hozz létre fiókot, és indíts elemzéseket több platformról: Reddit, Google,
            YouTube, Stack Overflow, X, GyakoriKérdések.
          </Typography>

          <Divider sx={{ my: 3, opacity: 0.4 }} />

          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            A regisztráció gyors, és azonnal elkezdheted a linkek feldolgozását.
          </Typography>
        </Box>

        {/* Jobb: üvegkártya form */}
        <Paper
          elevation={0}
          sx={{
            backdropFilter: 'blur(10px)',
            backgroundColor: (t) =>
              t.palette.mode === 'dark'
                ? 'rgba(18, 22, 30, 0.75)'
                : 'rgba(255,255,255,0.72)',
            border: '1px solid',
            borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.08)'),
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            boxShadow: (t) =>
              t.palette.mode === 'dark'
                ? '0 10px 30px rgba(0,0,0,.45)'
                : '0 10px 30px rgba(0,0,0,.08)',
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Regisztráció
          </Typography>

          {successMsg && (
            <Typography
              sx={{
                mt: 1,
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: (t) =>
                  t.palette.mode === 'dark' ? 'rgba(56,142,60,.20)' : 'rgba(76,175,80,.15)',
                border: '1px solid',
                borderColor: 'success.main',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              {successMsg}
            </Typography>
          )}

          {error && (
            <Typography
              color="error"
              sx={{
                mt: 1,
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: (t) =>
                  t.palette.mode === 'dark'
                    ? 'rgba(211,47,47,.18)'
                    : 'rgba(244,67,54,.15)',
                border: '1px solid',
                borderColor: 'error.main',
                fontSize: '0.875rem',
                fontWeight: 500,
                whiteSpace: 'pre-wrap',
              }}
            >
              {error}
            </Typography>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              disabled={!!successMsg || submitting}
            />

            <TextField
              label="Megjelenített név (opcionális)"
              fullWidth
              margin="normal"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setError('');
              }}
              disabled={!!successMsg || submitting}
              placeholder="Pl.: Kovács Anna"
            />

            <TextField
              label="Jelszó"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              disabled={!!successMsg || submitting}
            />

            <TextField
              label="Jelszó megerősítése"
              type="password"
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError('');
              }}
              disabled={!!successMsg || submitting}
            />

            <Divider sx={{ my: 2, opacity: 0.4 }} />

            {/* Profilkép feltöltés mint a Settings-ben */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Avatar
                src={preview || '/profile-placeholder.png'}
                sx={{ width: 64, height: 64, fontSize: 20 }}
              >
                {initials}
              </Avatar>

              <Button component="label" variant="outlined" disabled={!!successMsg || submitting}>
                Profilkép kiválasztása (opcionális)
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>

              {file && (
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {file.name}
                </Typography>
              )}
            </Stack>

            <Button
              type="submit"
              variant="contained"
              sx={{ mt: 1 }}
              fullWidth
              disabled={!!successMsg || submitting}
            >
              {submitting ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <CircularProgress size={18} />
                  <span>Feldolgozás…</span>
                </Stack>
              ) : (
                'Regisztráció'
              )}
            </Button>
          </Box>

          <Typography variant="body2" sx={{ mt: 2 }}>
            Már van fiókod?{' '}
            <MuiLink component={RouterLink} to="/login">
              Jelentkezz be
            </MuiLink>
          </Typography>

          <Divider sx={{ my: 3, opacity: 0.4 }} />
          {/* platform-csík (kollázs képeiből) */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, minmax(0,1fr))',
              gap: 1.5,
              alignItems: 'center',
              opacity: 0.9,
            }}
          >
            {['/reddit.png','/google.png','/youtube.png','/stacko.png','/x.webp','/gyik.png'].map((src, i) => (
              <Box key={i} component="img" src={src} alt="brand" sx={{ width: 26, height: 26, mx: 'auto', filter: 'grayscale(1)' }} />
            ))}
          </Box>
        </Paper>
      </Box>

      {/* Snack – opcionális, ha szeretnél rövid visszajelzést a sikerről/hibáról
          (itt csak success redirect van felül, ezért nem muszáj) */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={1500}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
